<template>
  <div class="page-container">
    <!-- 筛选区域 -->
    <Filter :params="params" @search="searchData">
      <template #handle>
        <el-form-item>
          <el-button icon="el-icon-plus" type="danger" @click="handleAdd">
            {{ $t('smartRouting.danceLibrary.add') }}
          </el-button>
        </el-form-item>
      </template>
    </Filter>

    <!-- 表格区域 -->
    <Table
      ref="tableRef"
      :tableData="tableDataList"
      :total="data.total"
      @update="updatePage"
      @sort-change="handleSortChange"
    >
      <!-- 编码 -->
      <el-table-column prop="code" :label="$t('smartRouting.danceLibrary.code')"/>

      <!-- 名称 -->
      <el-table-column prop="name" :label="$t('smartRouting.danceLibrary.name')"/>

      <!-- 适配机型 -->
      <el-table-column prop="model" :label="$t('smartRouting.danceLibrary.model')">
        <template #default="{ row }">
          <el-tag
            v-for="(item, index) in splitStr(row.model)"
            :key="index"
            size="small"
            style="margin: 2px;"
          >
            {{ item }}
          </el-tag>
        </template>
      </el-table-column>

      <!-- 标签 -->
      <el-table-column prop="tags" :label="$t('smartRouting.danceLibrary.tags')">
        <template #default="{ row }">
          <el-tag
            v-for="(tag, index) in splitStr(row.tags)"
            :key="index"
            size="small"
            type="primary"
            style="margin: 2px;"
          >
            {{ tag }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="createBy" :label="$t('smartRouting.danceLibrary.createBy')"/>
      <el-table-column prop="createTime" :label="$t('smartRouting.danceLibrary.createTime')"/>
      <el-table-column prop="updateBy" :label="$t('smartRouting.danceLibrary.updateBy')"/>
      <el-table-column prop="updateTime" :label="$t('smartRouting.danceLibrary.updateTime')"/>

      <!-- 操作（保持原样，继续用 mapCenter 的 i18n） -->
      <el-table-column label="操作" fixed="right" width="140">
        <template #default="{ row }">
          <el-tooltip :content="$t('smartRouting.mapCenter.detail')" placement="top">
            <el-button type="text" size="small" icon="el-icon-view" @click="handleView(row)">
              <!-- {{ $t('smartRouting.mapCenter.detail') }} -->
            </el-button>
          </el-tooltip>
          <el-tooltip :content="$t('smartRouting.mapCenter.edit')" placement="top">
            <el-button type="text" size="small" icon="el-icon-edit" @click="handleEdit(row)">
              <!-- {{ $t('smartRouting.mapCenter.edit') }} -->
            </el-button>
          </el-tooltip>
          <el-tooltip :content="$t('smartRouting.mapCenter.pathPlanning.routeManagement.deliver')" placement="top">
            <el-button type="text" size="small" icon="el-icon-position" @click="openDeliverDialog(row)">
              <!-- {{ $t('smartRouting.mapCenter.pathPlanning.routeManagement.deliver') }} -->
            </el-button>
          </el-tooltip>
          <el-tooltip :content="$t('smartRouting.mapCenter.delete')" placement="top">
            <el-button type="text" style="color: red;" size="small" icon="el-icon-delete" @click="handleDelete(row)">
              <!-- {{ $t('smartRouting.mapCenter.delete') }} -->
            </el-button>
          </el-tooltip>
        </template>
      </el-table-column>
    </Table>

    <!-- 新增/编辑/查看弹窗 -->
    <el-dialog
      :title="dialogTitle"
      v-model="data.dialogVisible"
      destroy-on-close
      :modal="true"
      :append-to-body="true"
      width="560px"
    >
      <el-form
        :model="formData"
        label-width="100px"
        ref="formRef"
      >
        <!-- 名称 -->
        <el-form-item :label="$t('smartRouting.danceLibrary.name')" :required="dialogMode !== 'view'">
          <el-input
            v-model="formData.name"
            :placeholder="$t('smartRouting.danceLibrary.danceNamePlaceholder')"
            :disabled="dialogMode === 'view'"
            :class="{ 'view-input': dialogMode === 'view' }"
          />
        </el-form-item>

        <!-- 标签：查看模式渲染为 tag，编辑模式为输入框 -->
        <el-form-item :label="$t('smartRouting.danceLibrary.tags')">
          <template v-if="dialogMode === 'view'">
            <el-tag
              v-for="(tag, index) in splitStr(formData.tags)"
              :key="index"
              size="small"
              type="primary"
              effect="light"
              class="view-tag"
            >
              {{ tag }}
            </el-tag>
          </template>
          <el-input
            v-else
            v-model="formData.tags"
            :placeholder="$t('smartRouting.danceLibrary.tagsPlaceholder')"
          />
        </el-form-item>

        <!-- 舞种文件 -->
        <el-form-item
          :label="$t('smartRouting.danceLibrary.file')"
          :required="dialogMode !== 'view'"
          prop="filePath"
        >
          <!-- 查看模式：只读输入框 -->
          <template v-if="dialogMode === 'view'">
            <el-input
              :model-value="formData.fileName || '-'"
              disabled
              class="view-input"
            />
          </template>

          <!-- 新增/编辑模式：上传框 -->
          <template v-else>
            <div class="upload-box" @click="triggerUpload">
              <div class="upload-info">
                <div v-if="formData.fileName" class="file-name">
                  <span>{{ formData.fileName }}</span>
                </div>
                <div v-else class="upload-placeholder">
                  {{ $t('smartRouting.danceLibrary.filePlaceholder') }}
                </div>
                <div class="upload-tip">{{ $t('smartRouting.danceLibrary.fileTip') }}</div>
              </div>
              <el-button
                size="small"
                type="primary"
                plain
                @click.stop="triggerUpload"
              >
                {{ formData.filePath
                  ? $t('smartRouting.danceLibrary.fileReupload')
                  : $t('smartRouting.danceLibrary.fileSelect') }}
              </el-button>
            </div>
            <input
              ref="fileInputRef"
              type="file"
              accept=".tar.gz,.gz,.tar"
              style="display: none"
              @change="handleFileChange"
            />
          </template>
        </el-form-item>

        <!-- 适配机型 -->
        <el-form-item :label="$t('smartRouting.danceLibrary.model')" :required="dialogMode !== 'view'">
          <template v-if="dialogMode === 'view'">
            <el-input
              :model-value="formData.model || '-'"
              disabled
              class="view-input"
            />
          </template>
          <el-input
            v-else
            v-model="formData.model"
            :placeholder="$t('smartRouting.danceLibrary.modelPlaceholder')"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <!-- 查看模式 -->
        <template v-if="dialogMode === 'view'">
          <el-button size="mini" @click="data.dialogVisible = false">
            {{ $t('smartRouting.danceLibrary.close') }}
          </el-button>
        </template>

        <!-- 新增/编辑模式 -->
        <template v-else>
          <el-button size="mini" @click="data.dialogVisible = false">
            {{ $t('smartRouting.danceLibrary.cancel') }}
          </el-button>
          <el-button type="primary" size="mini" @click="handleSubmit">
            {{ dialogMode === 'edit'
              ? $t('smartRouting.danceLibrary.save')
              : $t('smartRouting.danceLibrary.confirm') }}
          </el-button>
        </template>
      </template>
    </el-dialog>

    <!-- 舞种库下发组件 -->
    <DanceDeliverDialog
      v-model="data.dialogDeliverVisible"
      :danceInfo="deliverRow"
      @success="handleDeliverSuccess"
    />
  </div>
</template>

<script>
import { request } from "@/utils/request";
import { ElMessage, ElMessageBox } from "element-plus";
import Table from "@/components/table/index";
import Filter from "@/components/table/filter";
import DanceDeliverDialog from "./components/danceDeliver.vue";
import { ref, reactive, toRefs, computed } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";
import { ossUpload } from "@/utils/oss";

import {
  reqDancePage,
  reqDanceCreate,
  reqDanceUpdate,
  reqDanceDelete,
  reqDeployDance,
  reqDeployPrecheck
} from '@/api/dance'

// ===== 常量映射 =====
const modelMap = {
  'π仔': 'π仔',
  'JAKA π': 'JAKA π',
  'JAKA Lumi': 'JAKA Lumi',
};

const tagMap = {
  '爆款': '爆款',
  '商用': '商用',
  '文化': '文化',
  '展演': '展演',
  '迎宾': '迎宾',
  '礼仪': '礼仪',
  '安静': '安静',
};

export default {
  name: 'DanceLibrary',
  components: {
    Table,
    Filter,
    DanceDeliverDialog,
  },
  setup() {
    const store = useStore();
    const { t } = useI18n();

    const state = reactive({
      tableRef: null,
      curLang: computed(() => store.state.lang),
      authList: JSON.parse(window.localStorage.getItem('authList') || '[]'),
      sortData: {},
    });

    // ===== 筛选参数配置 =====
    const params = [
      {
        key: 'name',
        value: '',
        label: t('smartRouting.danceLibrary.name'),
        placeholder: t('smartRouting.danceLibrary.namePlaceholder'),
      },
      {
        key: 'tag',
        value: '',
        label: t('smartRouting.danceLibrary.tags'),
        placeholder: t('smartRouting.danceLibrary.tagPlaceholder'),
      },
    ];

    // ===== 数据状态 =====
    const data = reactive({
      page: 1,
      size: 10,
      total: 0,
      dialogVisible: false,
      dialogDeliverVisible: false,
    });

    const tableDataList = ref([]);
    const formRef = ref(null);
    const fileInputRef = ref(null);
    const dialogMode = ref('add'); // add | edit | view
    const dialogTitle = ref(t('smartRouting.danceLibrary.add'));
    const editId = ref(null);
    const editCode = ref(null);
    const deliverRow = ref({});
    const query = reactive({});

    const formData = reactive({
      name: '',
      tags: '',
      fileName: '',
      filePath: '',
      fileUrl: '',
      model: '',
    });

    // ===== 逗号分隔字符串 → 数组 =====
    const splitStr = (str) => {
      if (!str) return [];
      if (Array.isArray(str)) return str;
      return str.split(/[,，]/).map((s) => s.trim()).filter(Boolean);
    };

    // ===== API 请求 =====
    const getData = async () => {
      try {
        const res = await reqDancePage({
          ...query,
          ...state.sortData,
          page: data.page,
          size: data.size,
        });
        tableDataList.value = res.records || [];
        data.total = res.total || 0;
      } catch (error) {
        console.error('getDanceListFailed:', error);
        ElMessage.error(t('smartRouting.danceLibrary.getDataFailed'));
      }
    };

    // ===== 搜索 =====
    const searchData = (searchParams) => {
      Object.assign(query, searchParams);
      data.page = 1;
      getData();
    };

    // ===== 分页 =====
    const updatePage = (page, size) => {
      data.page = page || 1;
      data.size = size || 10;
      getData();
    };

    // ===== 排序 =====
    const handleSortChange = ({ prop, order }) => {
      const orderMap = { ascending: 'asc', descending: 'desc' };
      state.sortData = {
        sortField: prop,
        sort: orderMap[order] || '',
      };
      getData();
    };

    // ===== 重置表单 =====
    const resetForm = () => {
      formData.name = '';
      formData.tags = '';
      formData.fileName = '';
      formData.filePath = '';
      formData.fileUrl = '';
      formData.model = '';
      editCode.value = null;
      if (fileInputRef.value) {
        fileInputRef.value.value = '';
      }
    };

    // ===== 新增 =====
    const handleAdd = () => {
      dialogMode.value = 'add';
      dialogTitle.value = t('smartRouting.danceLibrary.add');
      editId.value = null;
      editCode.value = null;
      resetForm();
      data.dialogVisible = true;
    };

    // ===== 编辑 =====
    const handleEdit = (row) => {
      dialogMode.value = 'edit';
      dialogTitle.value = t('smartRouting.danceLibrary.edit');
      editId.value = row.id;
      editCode.value = row.code;
      formData.name = row.name;
      formData.tags = row.tags || '';
      formData.fileName = row.fileName || '';
      formData.filePath = row.filePath || '';
      formData.fileUrl = row.fileUrl || '';
      formData.model = row.model || '';
      data.dialogVisible = true;
    };

    // ===== 查看 =====
    const handleView = (row) => {
      dialogMode.value = 'view';
      dialogTitle.value = t('smartRouting.danceLibrary.view');
      editId.value = row.id;
      editCode.value = row.code;
      formData.name = row.name;
      formData.tags = row.tags || '';
      formData.fileName = row.fileName || '';
      formData.filePath = row.filePath || '';
      formData.fileUrl = row.fileUrl || '';
      formData.model = row.model || '';
      data.dialogVisible = true;
    };

    // ===== 触发文件选择 =====
    const triggerUpload = () => {
      fileInputRef.value?.click();
    };

    // ===== 文件变更（ossUpload 真实上传） =====
    const handleFileChange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // 校验 tar.gz 格式
      const isTarGz = /\.(tar\.gz|tgz)$/i.test(file.name);
      if (!isTarGz) {
        ElMessage.warning(t('smartRouting.danceLibrary.fileFormatWarning'));
        e.target.value = '';
        return;
      }

      formData.fileName = file.name;
      formData.filePath = 'smart/dance/' + dayjs().format('YYYY/MM/DD/') + file.name;

      try {
        formData.fileUrl = await ossUpload(file.name, file, {
          path: 'smart/dance/' + dayjs().format('YYYY/MM/DD'),
          timestamp: false,
        });
        ElMessage.success(t('smartRouting.danceLibrary.uploadSuccess'));
      } catch (error) {
        console.error('uploadFailed:', error);
        ElMessage.error(t('smartRouting.danceLibrary.uploadFailed'));
        formData.fileName = '';
        formData.filePath = '';
        formData.fileUrl = '';
      } finally {
        e.target.value = '';
      }
    };

    // ===== 提交 =====
    const handleSubmit = async () => {
      if (!formData.name) {
        ElMessage.warning(t('smartRouting.danceLibrary.nameRequired'));
        return;
      }
      if (!formData.fileName) {
        ElMessage.warning(t('smartRouting.danceLibrary.fileRequired'));
        return;
      }
      if (!formData.model) {
        ElMessage.warning(t('smartRouting.danceLibrary.modelRequired'));
        return;
      }

      const payload = {
        name: formData.name,
        tags: formData.tags || '',
        fileName: formData.fileName,
        filePath: formData.filePath,
        fileUrl: formData.fileUrl,
        model: formData.model || '',
      };

      try {
        if (dialogMode.value === 'edit') {
          await reqDanceUpdate({
            id: editId.value,
            code: editCode.value,
            ...payload,
          });
          ElMessage.success(t('smartRouting.danceLibrary.editSuccess'));
        } else {
          await reqDanceCreate(payload);
          ElMessage.success(t('smartRouting.danceLibrary.addSuccess'));
        }
        data.dialogVisible = false;
        getData();
      } catch (error) {
        console.error('submitFailed:', error);
        ElMessage.error(
          dialogMode.value === 'edit'
            ? t('smartRouting.danceLibrary.editFailed')
            : t('smartRouting.danceLibrary.addFailed')
        );
      }
    };

    // ===== 删除（确认后用 code 删除） =====
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm(
          t('smartRouting.danceLibrary.deleteConfirm', { name: row.name }),
          t('smartRouting.danceLibrary.deleteConfirmTitle'),
          {
            confirmButtonText: t('smartRouting.danceLibrary.confirm'),
            cancelButtonText: t('smartRouting.danceLibrary.cancel'),
            type: 'warning',
          }
        );
        await reqDanceDelete(row.code);
        ElMessage.success(t('smartRouting.danceLibrary.deleteSuccess'));
        getData();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('deleteFailed:', error);
          ElMessage.error(t('smartRouting.danceLibrary.deleteFailed'));
        }
      }
    };

    // ===== 下发 =====
    const openDeliverDialog = (row) => {
      deliverRow.value = {
        id: row.id,
        name: row.name,
        fileName: row.fileName,
        filePath: row.filePath,
        fileUrl: row.fileUrl,
        model: row.model,
        tags: row.tags,
        code: row.code
      };
      data.dialogDeliverVisible = true;
    };

    const handleDeliverSuccess = ({ danceId, deviceIds }) => {
      ElMessage.success(
        t('smartRouting.danceLibrary.deliverSuccess', {
          name: deliverRow.value.name,
          count: deviceIds.length,
        })
      );
      // 如需刷新列表可调用 getData()
      // getData();
    };

    // ===== 初始化加载 =====
    getData();

    return {
      ...toRefs(state),
      params,
      data,
      tableDataList,
      formRef,
      fileInputRef,
      dialogMode,
      dialogTitle,
      formData,
      editId,
      editCode,
      deliverRow,
      modelMap,
      tagMap,
      splitStr,
      searchData,
      updatePage,
      handleSortChange,
      handleAdd,
      handleEdit,
      handleView,
      handleSubmit,
      handleDelete,
      triggerUpload,
      handleFileChange,
      openDeliverDialog,
      handleDeliverSuccess,
    };
  },
};
</script>

<style lang="scss" scoped>
.page-container {
  height: 100%;
  padding: 50px 10px 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

/* ===== 上传区域（参考动作库样式） ===== */
.upload-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  background-color: #fafafa;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
}

.upload-box:hover {
  border-color: #409eff;
}

.upload-info {
  flex: 1;
}

.upload-placeholder {
  color: #c0c4cc;
}

.file-name {
  display: flex;
  align-items: center;
  color: #67c23a;
  margin-bottom: 6px;
}

.upload-tip {
  color: #909399;
  font-size: 12px;
}

/* ===== 查看模式样式 ===== */
.view-tag {
  margin-right: 8px;
}

/* 只读输入框：浅灰底、灰色文字 */
.view-input :deep(.el-input__wrapper) {
  background-color: #f5f7fa;
  box-shadow: 0 0 0 1px #e4e7ed inset;
}

.view-input :deep(.el-input__inner) {
  color: #606266;
  -webkit-text-fill-color: #606266;
}
</style>